/**
 * Custom staff role switches, shared by the web dashboard and the phone app
 * (#6135, plan docs/plans/2026-10-27-1252-feat-custom-staff-roles-plan.md R3,
 * R6, R13, R15, KTD10).
 *
 * The web keeps its gates in gym-admin-dashboard/src/utils/permissions/
 * staffPermissions.ts, which re-exports the switch list, the scope rule and
 * the no-access sentence from here. The phone app never decides a switch
 * itself: GET /api/mobile/staff-access answers which phone surfaces a custom
 * seat may open (mobileCustomSurfaces.ts on the server), and the app reads
 * that answer with mobileSeatAllows.
 *
 * Imports nothing: the phone app reaches this file through Metro
 * (sharedModuleStaysDependencyFree.test.ts).
 */

export const STAFF_PERMISSION_KEYS = [
  'teams_athletes',
  'messages',
  'staff',
  'classes_schedule',
  'prices',
  'time_hours',
  'reports',
  'gym_settings',
  'take_payments',
  'refunds_discounts',
  'see_money',
  'pay_payroll',
  'payouts_billing',
] as const;

export type StaffPermissionKey = (typeof STAFF_PERMISSION_KEYS)[number];

export type StaffPermissionSet = Readonly<Record<StaffPermissionKey, boolean>>;

/** R13: the one message a custom role holder sees on anything they cannot open. */
export const STAFF_NO_ACCESS_MESSAGE =
  "You don't have access to this. Please ask the gym owner or an administrator.";

/** R6: the switches that "All teams" / "Only their teams" applies to. */
export const TEAM_SCOPED_PERMISSION_KEYS: readonly StaffPermissionKey[] = [
  'teams_athletes',
  'messages',
  'classes_schedule',
  'reports',
];

export const STAFF_TEAM_SCOPES = ['all', 'assigned'] as const;

export type StaffTeamScope = (typeof STAFF_TEAM_SCOPES)[number];

export function customRoleUsesScope(permissions: StaffPermissionSet): boolean {
  return TEAM_SCOPED_PERMISSION_KEYS.some((key) => permissions[key]);
}

/** New roles and roles with no scoped switch are All teams (R6). */
export function effectiveTeamScope(permissions: StaffPermissionSet, scope: StaffTeamScope): StaffTeamScope {
  return customRoleUsesScope(permissions) ? scope : 'all';
}

/**
 * What the phone app shows a custom seat, one key per screen, menu item or
 * button (R15). The server decides each one from the web's own gate.
 */
export const MOBILE_STAFF_SURFACES = [
  'schedule',
  'teams',
  'class_attendance',
  'evaluations',
  'incident_reports',
  'competition_travel',
  'competitions',
  'communications_center',
  'staff_policies',
  'ticket_checkin',
  'roll_call',
  'manage_rsvps',
  'athlete_restrictions',
] as const;

export type MobileStaffSurface = (typeof MOBILE_STAFF_SURFACES)[number];

/** GET /api/mobile/staff-access: the caller's custom seat at one gym. */
export interface MobileCustomSeat {
  displayName: string | null;
  /** After effectiveTeamScope: a role with no team switch on is 'all'. */
  teamScope: StaffTeamScope;
  surfaces: MobileStaffSurface[];
  /** "Only their teams": the teams they coach or are Team staff on. Null for "All teams". */
  teamIds: string[] | null;
}

/** False for no seat, a seat still loading, and a failed read (fails closed). */
export function mobileSeatAllows(
  seat: Pick<MobileCustomSeat, 'surfaces'> | null | undefined,
  surface: MobileStaffSurface
): boolean {
  return !!seat && seat.surfaces.includes(surface);
}
